import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Blocks } from 'lucide-react';
import { EXTENSIONS } from '@/extensions/registry';
import { useExtensionsStore } from '@/stores/extensionsStore';
import { ExtensionSettingsForm } from './ExtensionSettingsForm';

interface ExtensionsSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Every extension, with its on/off switch and, when on, its settings. */
export function ExtensionsSettings({ open, onOpenChange }: ExtensionsSettingsProps) {
  const { rows, loaded, load, isEnabled, getSettings, setEnabled, saveSettings } = useExtensionsStore();
  const [activePanel, setActivePanel] = useState<string | null>(null);

  useEffect(() => {
    if (open && !loaded) load();
  }, [open, loaded, load]);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Blocks className="w-5 h-5" />
            Extensions
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Extensions bring outside content onto your canvas. Turn on the ones you want; they show up in the toolbar.
        </p>

        <div className="space-y-3">
          {EXTENSIONS.map(({ manifest, Icon }) => {
            const enabled = isEnabled(manifest.id);
            const action = manifest.actions[0];
            return (
              <div key={manifest.id} className="rounded-xl border border-border/60 p-3 space-y-3">
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${manifest.accent}1a`, color: manifest.accent }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm">{manifest.name}</span>
                      <Switch
                        checked={enabled}
                        onCheckedChange={(v) => setEnabled(manifest.id, v)}
                        aria-label={`Enable ${manifest.name}`}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{manifest.description}</p>
                    {action?.cost === 'paid' && action.dailyLimit && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Uses paid scraping credits. Limit: {action.dailyLimit} runs per day.
                      </p>
                    )}
                    {enabled && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 mt-2 text-xs"
                        onClick={() => {
                          onOpenChange(false);
                          setActivePanel(manifest.id);
                        }}
                      >
                        Open {manifest.name}
                      </Button>
                    )}
                  </div>
                </div>
                {enabled && Object.keys(manifest.settings).length > 0 && (
                  <div className="border-t border-border/50 pt-3">
                    <ExtensionSettingsForm
                      manifest={manifest}
                      values={getSettings(manifest.id)}
                      onSave={(values) => saveSettings(manifest.id, values)}
                      key={JSON.stringify(rows[manifest.id]?.settings ?? {})}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>

    {EXTENSIONS.map(({ manifest, Panel }) => (
      <Panel
        key={manifest.id}
        open={activePanel === manifest.id}
        onOpenChange={(o) => setActivePanel(o ? manifest.id : null)}
      />
    ))}
    </>
  );
}
